import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import LucideIcon from 'src/componentes/lucide-icon';

import css from 'src/utils/css';
import FocusTrap from 'src/componentes/focus-trap';

const Drawer = ({
  open,
  close,
  size: sizeSuggestion,
  position,
  header,
  title,
  icon,
  children,
  padding,
  actions,
  showBackdrop,
  zIndex,
  focusTrap,
  focusTrapPriority,
  headerStyle = {},
  bodyStyle = {},
  actionsStyle = {},
}) => {
  const [size] = useState(sizeSuggestion);
  const [display, setDisplay] = useState(open);

  const sizeValue = typeof size === 'number' ? `${size}px` : size;

  useEffect(() => {
    const body = document.body;
    let timeout;

    if (open) {
      body.classList.add('drawer-open');
      setDisplay(true);
    } else if (display) {
      timeout = setTimeout(() => {
        body.classList.remove('drawer-open');
        setDisplay(false);
      }, 500);
    }

    return () => {
      clearTimeout(timeout);
      body.classList.remove('drawer-open');
    };
  }, [open]);

  const classes = css`
    .backdrop {
      position: fixed;
      z-index: ${zIndex};
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.3) !important;
      display: ${display ? 'flex' : 'none'};
      animation: ${open ? 'fadeIn' : 'fadeOut'} 0.2s ease-in-out forwards;
    }

    .backdropOff {
      position: fixed;
      z-index: ${zIndex};
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      display: ${display ? 'flex' : 'none'};
      animation: ${open ? 'fadeIn' : 'fadeOut'} 0.2s ease-in-out forwards;
    }

    .container {
      max-width: 100%;
      flex: 1 0 auto;
      display: ${display ? 'flex' : 'none'};
      outline: 0;
      z-index: ${zIndex};
      position: fixed;
      flex-direction: column;
      background: #fff;
      box-shadow: 0px 8px 10px -5px rgb(0 0 0 / 20%),
        0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%);
      overflow: auto;
    }

    .header {
      display: flex;
      width: 100%;
      justify-content: space-between;
      padding: ${padding ? '15px 20px' : ''};
      padding-bottom: 0px;
      z-index: 3;
      position: sticky;
      top: 0;
      background: #fff;
      align-items: center;
    }

    .actions {
      display: flex;
      padding: ${padding ? '15px 20px' : ''};
    }

    .headerTitle {
      display: flex;
      align-items: center;
      svg {
        margin-right: 10px;
      }
      h4 {
        margin-bottom: 0;
        font-size: 16px;
      }
    }

    .body {
      padding: ${padding ? '15px 20px' : ''};
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
    }

    .closeIcon {
      cursor: pointer;
    }

    .checkIcon {
      cursor: pointer;
      color: #3b82ec;
    }

    .right {
      top: 0;
      right: -${sizeValue};
      width: ${sizeValue};
      height: 100%;
      animation: ${open ? 'openRight' : 'closeRight'} 0.5s ease-in-out forwards;
    }

    .left {
      top: 0;
      left: -${sizeValue};
      width: ${sizeValue};
      height: 100%;
      animation: ${open ? 'openLeft' : 'closeLeft'} 0.5s ease-in-out forwards;
    }

    .bottom {
      bottom: 0;
      left: 0;
      width: 100%;
      height: ${sizeValue};
      animation: ${open ? 'openBottom' : 'closeBottom'} 0.5s ease-in-out
        forwards;
    }
  `;

  const drawer = (
    <div className={clsx(classes.container, classes[position])}>
      {header && (
        <div className={classes.header} style={headerStyle}>
          <div className={classes.headerTitle}>
            {icon}
            <h4 className="text-primary">{title}</h4>
          </div>
          <LucideIcon
            icon="X"
            className={classes.closeIcon}
            onClick={close}
            size={18}
          />
        </div>
      )}
      <div className={classes.body} style={bodyStyle}>
        {children}
      </div>
      {actions && (
        <div className={classes.actions} style={actionsStyle}>
          {actions}
        </div>
      )}
    </div>
  );

  if (!display) return <></>;

  return createPortal(
    <>
      <div
        onClick={close}
        className={showBackdrop ? classes.backdrop : classes.backdropOff}
      />
      {focusTrap ? (
        <FocusTrap component="div" trap={focusTrapPriority}>
          {drawer}
        </FocusTrap>
      ) : (
        drawer
      )}
    </>,
    document.body
  );
};

Drawer.defaultProps = {
  size: 430,
  position: 'right',
  header: true,
  icon: null,
  padding: true,
  actions: null,
  showBackdrop: true,
  zIndex: 1000,
  focusTrap: false,
  focusTrapPriority: 1,
};

export default Drawer;
